import { useState, useEffect } from 'react';
import { ProfileResponse } from '../../utils/types';
import ProfilePreview from '../Discover/ProfilePreview';
import DiscoverPreviewFrame from '../Discover/ProfilePreview/DiscoverPreviewFrame';
import { asDiscoverProfile } from '../Discover/ProfilePreview/asDiscoverProfile';
import JWModal from '../brand/JWModal';
import ViewHighlightsModal from './ViewHighlightsModalLazy';
import { HighlightType } from '../../types/profile';
import { Link } from 'react-router-dom';

interface ViewProfileAsOthersModalProps {
    profileInfo: any;
    userInfo: any;
    show: boolean;
    onClose: () => void;
    setShow: (show: boolean) => void;
}

const ViewProfileAsOthersModal = ({ profileInfo, userInfo, show, onClose, setShow }: ViewProfileAsOthersModalProps) => {
    const [profile, setProfile] = useState<ProfileResponse | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [selectedHighlight, setSelectedHighlight] = useState<HighlightType | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!profileInfo || !userInfo) return;

        setProfile(asDiscoverProfile(profileInfo, userInfo) as ProfileResponse);
    }, [profileInfo, userInfo]);

    const handleHighlightClick = (highlight: HighlightType, index: number) => {
        setSelectedHighlight(highlight);
        setSelectedIndex(index);
        setShowViewModal(true);
    };

    const handleNavigate = (direction: 'prev' | 'next') => {
        if (!profile?.highlights) return;

        const newIndex = direction === 'prev'
            ? (selectedIndex - 1 + profile.highlights.length) % profile.highlights.length
            : (selectedIndex + 1) % profile.highlights.length;

        setSelectedIndex(newIndex);
        setSelectedHighlight(profile.highlights[newIndex] as HighlightType);
    };

    const hasMultipleHighlights = Boolean(profile?.highlights && profile.highlights.length > 1);

    const modalBody = profile ?
        <DiscoverPreviewFrame
            header={
                <h2 className='text-[20px] font-normal text-center mb-4'>
                    Here's how your profile appears to others on the{' '}
                    <Link to="/discover" className="text-[#FF4D4D] underline hover:underline">
                        Discover page
                    </Link>
                    {' '}and search results
                </h2>
            }
        >
            <ProfilePreview
                profile={profile}
                isGridView={false}
                onHighlightClick={handleHighlightClick}
                hideLikeButton={true}
            />
        </DiscoverPreviewFrame>
        : <></>;

    return (
        <>
            <JWModal
                title={''}
                show={show}
                onClose={onClose}
                setShow={setShow}
                wrapperClassName={isMobile ?
                    'discover-preview-modal fixed inset-0 w-full h-full rounded-none overflow-y-auto' :
                    'discover-preview-modal w-max max-w-[calc(100vw-1rem)] max-h-[90vh] overflow-y-auto'
                }
            >
                {modalBody}
            </JWModal>
            <ViewHighlightsModal
                show={showViewModal}
                setShow={setShowViewModal}
                highlight={selectedHighlight}
                onClose={() => setShowViewModal(false)}
                onNavigate={handleNavigate}
                canNavigatePrev={hasMultipleHighlights}
                canNavigateNext={hasMultipleHighlights}
                isOwnHighlight={false}
            />
        </>
    );
};

// Helper function to generate a unique identifier for the highlight
const getHighlightIdentifier = (highlight: any) => {
    if (!highlight) return '';
    return `${highlight.mediaUrl || ''}-${highlight.url || ''}-${highlight.description || ''}`;
};

export default ViewProfileAsOthersModal;
